<template>
  <nav class="navbar" :class="{ scrolled: isScrolled, 'menu-open': menuOpen }">
    <div class="nav-container">
      <!-- Logo -->
      <router-link to="/" class="nav-logo" aria-label="点石引擎 Dianshi Engine 首页">
        <img :src="brandLogoSrc" alt="点石引擎 Dianshi Engine" class="nav-logo-img" width="240" height="48" />
      </router-link>

      <!-- Desktop Nav -->
      <ul class="nav-links">
        <li v-for="item in navItems" :key="item.label">
          <router-link v-if="item.to" :to="item.to" class="nav-link" active-class="active" :exact="item.to === '/'">{{ item.label }}</router-link>
          <a v-else :href="item.href" class="nav-link" :class="{ active: item.active }">{{ item.label }}</a>
        </li>
      </ul>

      <!-- Right Actions -->
      <div class="nav-actions">
        <a
          class="nav-jianying-download"
          :href="jianyingAssistantDownloadUrl"
          target="_blank"
          rel="noopener noreferrer"
          title="下载剪映小助手 Windows 客户端"
        >
          <svg class="nav-jianying-download__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="2" y="3" width="20" height="14" rx="2" />
            <path d="M8 21h8" />
            <path d="M12 17v4" />
            <path d="M12 7v5" />
            <path d="M9 10l3 3 3-3" />
          </svg>
          <span class="nav-jianying-download__text">剪映小助手下载</span>
        </a>
        <template v-if="user">
          <router-link
            to="/user-center?tab=points"
            class="nav-credits"
            :title="`剩余积分 ${navUserScoreDisplay}，前往积分充值`"
            aria-label="剩余积分，前往积分充值"
          >
            <svg class="nav-credits-icon" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
              <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z"/>
            </svg>
            <span class="nav-credits-val">{{ navUserScoreDisplay }}</span>
          </router-link>
        </template>
        <button class="theme-toggle" @click="$emit('toggle-theme')" :title="isDark ? '切换白天模式' : '切换夜间模式'">
          <transition name="icon-fade" mode="out-in">
            <svg v-if="isDark" key="sun" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
              <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
            <svg v-else key="moon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          </transition>
        </button>
        <template v-if="user">
          <div
            class="nav-user-wrap"
            ref="userWrap"
            @mouseenter="onUserWrapEnter"
            @mouseleave="onUserWrapLeave">
            <button class="nav-user-btn" type="button" :aria-expanded="userDropdownOpen" aria-haspopup="true">
              <div
                class="nav-user-avatar"
                :class="{ 'nav-user-avatar--img': !!navAvatarUrl }"
                :style="navAvatarUrl ? {} : { background: 'linear-gradient(135deg,#2563EB,#06B6D4)' }"
              >
                <img v-if="navAvatarUrl" :src="navAvatarUrl" alt="" class="nav-user-avatar-img" />
                <span v-else class="nav-user-initial">{{ navAvatarLetter }}</span>
              </div>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" :class="{ rotated: userDropdownOpen }"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            <transition name="dropdown">
              <div
                v-if="userDropdownOpen"
                class="nav-user-dropdown"
                @mouseenter="onUserWrapEnter"
                @mouseleave="onUserWrapLeave">
                <div class="nav-dropdown-header">
                  <div
                    class="nd-avatar"
                    :class="{ 'nd-avatar--img': !!navAvatarUrl }"
                    :style="navAvatarUrl ? {} : { background: 'linear-gradient(135deg,#2563EB,#06B6D4)' }"
                  >
                    <img v-if="navAvatarUrl" :src="navAvatarUrl" alt="" class="nd-avatar-img" />
                    <span v-else class="nd-avatar-initial">{{ navAvatarLetter }}</span>
                  </div>
                  <div class="nd-info">
                    <div class="nd-name-row">
                      <span class="nd-name">{{ navDisplayName }}</span>
                      <span
                        v-if="navMemberLevelLabel"
                        class="nd-member-badge"
                        :class="'nd-member-badge--' + navMemberBadgeKind"
                      >{{ navMemberLevelLabel }}</span>
                    </div>
                    <div class="nd-id">ID: {{ user && user.profile ? user.profile.userId : '-' }}</div>
                    <div v-if="navDisplayPhone" class="nd-phone">{{ navDisplayPhone }}</div>
                  </div>
                </div>
                <div class="nav-dropdown-divider"></div>
                <router-link to="/user-center" class="nav-dropdown-item" @click.native="closeUserDropdown">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                  个人中心
                </router-link>
                <router-link :to="resourceLibraryTo" class="nav-dropdown-item" @click.native="closeUserDropdown">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
                  我的资产
                </router-link>
                <button class="nav-dropdown-item nav-dropdown-logout" @click="doLogout">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                  退出登录
                </button>
              </div>
            </transition>
          </div>
        </template>
        <router-link v-else to="/login" class="btn-primary nav-cta">登录/注册</router-link>
        <button class="hamburger" @click="menuOpen = !menuOpen" :class="{ open: menuOpen }">
          <span></span><span></span><span></span>
        </button>
      </div>
    </div>

      <!-- Mobile Menu -->
    <transition name="slide-down">
      <div v-if="menuOpen" class="mobile-menu">
        <template v-for="item in navItems">
          <router-link v-if="item.to" :key="item.label" :to="item.to" class="mobile-link" active-class="active" :exact="item.to === '/'" @click.native="menuOpen = false">{{ item.label }}</router-link>
          <a v-else :key="item.label + '_a'" :href="item.href" class="mobile-link" @click="menuOpen = false">{{ item.label }}</a>
        </template>
        <a
          :href="jianyingAssistantDownloadUrl"
          class="mobile-jianying-download"
          target="_blank"
          rel="noopener noreferrer"
          @click="menuOpen = false"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="2" y="3" width="20" height="14" rx="2" />
            <path d="M8 21h8" />
            <path d="M12 17v4" />
            <path d="M12 7v5" />
            <path d="M9 10l3 3 3-3" />
          </svg>
          <span>剪映小助手下载</span>
        </a>
        <template v-if="user">
          <router-link
            to="/user-center?tab=points"
            class="mobile-credits-bar"
            aria-label="剩余积分，前往积分充值"
            @click.native="menuOpen = false"
          >
            <svg class="mobile-credits-icon" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
              <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z"/>
            </svg>
            <span class="mobile-credits-label">剩余积分</span>
            <span class="mobile-credits-val">{{ navUserScoreDisplay }}</span>
          </router-link>
        </template>
        <div class="mobile-actions">
          <router-link v-if="!user" to="/login" class="btn-primary" style="width:100%;justify-content:center">登录/注册</router-link>
          <template v-else>
            <router-link to="/user-center" class="btn-primary" style="width:100%;justify-content:center" @click.native="menuOpen = false">个人中心</router-link>
            <router-link :to="resourceLibraryTo" class="mobile-link mobile-link--resource" @click.native="menuOpen = false">我的资产</router-link>
          </template>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script>
import { getUser, logout, authBus, resolveUserMobile } from '@/utils/auth'
import { refreshUserMemberAndProfile } from '@/utils/userProfileRefresh'
import { AUTH_RELOGIN_MESSAGE } from '@/utils/request'
import { vipLevelToLabel, vipLevelBadgeKind } from '@/utils/memberLevel'
import { getBrandLogoSrc } from '@/constants/brand'
import { getAgentRouteName } from '@/views/digital-employee/agentRegistry'

const DEFAULT_EMP_ID = '2'

export default {
  name: 'TheNavbar',
  props: { isDark: Boolean },
  emits: ['toggle-theme'],
  data() {
    return {
      isScrolled: false,
      menuOpen: false,
      user: getUser(),
      /** 与个人中心 getUserMemberInfo 一致，用于展示会员等级徽章 */
      memberInfo: null,
      userDropdownOpen: false,
      userDropdownCloseTimer: null,
      /** 剪映小助手 Windows 安装包（固定 OSS 链接） */
      jianyingAssistantDownloadUrl:
        'https://gugushiji.oss-cn-beijing.aliyuncs.com/000a/%E5%89%AA%E6%98%A0%E8%8D%89%E7%A8%BF%E5%88%9B%E5%BB%BA%E5%B7%A5%E5%85%B7_5.0.5%20.exe',
      navItems: [
        { label: '首页', to: '/' },
        { label: '工作流市场', to: '/workflow-store' },
        { label: '智能体中心', to: '/agents-center' },
        { label: '数字员工', to: '/digital-employee' },
        { label: '创业项目', to: '/cases' },
        { label: '关于我们', to: '/about' },
        { label: '定价', to: '/pricing' },
      ]
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll)
    authBus.$on('user-login', this.onUserLogin)
    authBus.$on('user-logout', this.onUserLogout)
    authBus.$on('member-info-updated', this.applyMemberInfo)
    this.fetchMemberInfo()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
    authBus.$off('user-login', this.onUserLogin)
    authBus.$off('user-logout', this.onUserLogout)
    authBus.$off('member-info-updated', this.applyMemberInfo)
  },
  computed: {
    brandLogoSrc() {
      return getBrandLogoSrc(this.isDark)
    },
    /** 与 UserCenter 一致：规范化 profile.avatarUrl */
    navAvatarUrl() {
      const u = this.user
      if (!u) return ''
      const raw = u.profile && u.profile.avatarUrl
      if (raw == null || raw === '') return ''
      const s = String(raw).trim()
      if (!s || s === 'undefined' || s === 'null') return ''
      if (/^https?:\/\//i.test(s)) return s
      if (s.startsWith('//')) return `https:${s}`
      if (s.startsWith('/')) return s
      return s
    },
    navDisplayPhone() {
      return resolveUserMobile(this.user)
    },
    /** getUserMemberInfo.userScore：剩余积分 */
    navUserScoreDisplay() {
      const m = this.memberInfo
      if (!m || m.userScore == null || m.userScore === '') return '0'
      const n = Number(m.userScore)
      if (!Number.isFinite(n)) return '0'
      return String(Math.max(0, Math.floor(n)))
    },
    resourceLibraryTo() {
      return {
        name: getAgentRouteName('assets'),
        params: { empId: DEFAULT_EMP_ID },
      }
    },
    navAvatarLetter() {
      const u = this.user
      if (!u) return '用'
      const nick = (u.profile && u.profile.nickname) || u.nickname
      const phone = resolveUserMobile(u)
      const ch = (nick && String(nick).trim()) || phone || '用'
      return String(ch).charAt(0) || '用'
    },
    navDisplayName() {
      const u = this.user
      if (!u) return '用户'
      const n = (u.profile && u.profile.nickname) || u.nickname
      return (n && String(n).trim()) || '用户'
    },
    navMemberLevelLabel() {
      if (!this.memberInfo || this.memberInfo.vipLevel == null) return ''
      return vipLevelToLabel(this.memberInfo.vipLevel)
    },
    navMemberBadgeKind() {
      return vipLevelBadgeKind(this.memberInfo && this.memberInfo.vipLevel)
    },
  },
  methods: {
    applyMemberInfo(data) {
      if (data && typeof data === 'object') this.memberInfo = data
    },
    async fetchMemberInfo() {
      if (!getUser()) {
        this.memberInfo = null
        return
      }
      try {
        const { memberData } = await refreshUserMemberAndProfile()
        if (memberData) this.memberInfo = memberData
        this.user = getUser()
      } catch (err) {
        if (err?.message === AUTH_RELOGIN_MESSAGE) {
          this.handleLoginAbnormal(AUTH_RELOGIN_MESSAGE)
          return
        }
        /* 网络等临时失败由 member-info-updated 事件兜底 */
      }
    },
    handleLoginAbnormal(message = '登录异常，请重新登录') {
      logout()
      this.user = null
      this.memberInfo = null
      this.userDropdownOpen = false
      if (this.$message) this.$message.warning(message)
      const redirect = this.$route?.fullPath && this.$route.name !== 'Login'
        ? this.$route.fullPath
        : undefined
      this.$router.replace({
        name: 'Login',
        query: {
          ...(redirect ? { redirect } : {}),
          expired: '1',
        },
      }).catch(() => {})
    },
    onScroll() {
      this.isScrolled = window.scrollY > 20
    },
    onUserLogin(user) {
      this.user = user
      this.fetchMemberInfo()
    },
    onUserLogout() {
      this.user = null
      this.memberInfo = null
      this.userDropdownOpen = false
    },
    onUserWrapEnter() {
      if (this.userDropdownCloseTimer) {
        clearTimeout(this.userDropdownCloseTimer)
        this.userDropdownCloseTimer = null
      }
      this.userDropdownOpen = true
    },
    onUserWrapLeave() {
      this.userDropdownCloseTimer = setTimeout(() => {
        this.userDropdownOpen = false
        this.userDropdownCloseTimer = null
      }, 150)
    },
    closeUserDropdown() {
      this.userDropdownOpen = false
      if (this.userDropdownCloseTimer) {
        clearTimeout(this.userDropdownCloseTimer)
        this.userDropdownCloseTimer = null
      }
    },
    doLogout() {
      logout()
      this.user = null
      this.userDropdownOpen = false
      this.menuOpen = false
      this.$router.replace({ name: 'Login' }).catch(() => {})
    },
  },
}
</script>

<style scoped>
.navbar {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  background: transparent;
}
.navbar.scrolled {
  background: var(--bg-nav);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: var(--shadow);
}
.nav-container {
  max-width: 100%;
  margin: 0 auto;
  padding: 0 24px;
  height: 72px;
  display: flex;
  align-items: center;
  gap: 32px;
}
.nav-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  flex-shrink: 0;
}
.nav-logo-img {
  height: 48px;
  width: auto;
  max-width: min(280px, 48vw);
  display: block;
  object-fit: contain;
}
.nav-links {
  display: flex;
  align-items: center;
  gap: 4px;
  list-style: none;
  flex: 1;
  justify-content: center;
}
.nav-link {
  padding: 8px 14px;
  border-radius: 8px;
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.nav-link:hover, .nav-link.active {
  color: var(--primary);
  background: var(--primary-light);
}
.nav-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

/* 剪映小助手下载：主色紫蓝、描边+浅底，与主导航区分 */
.nav-jianying-download {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 10px;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #5b67d8;
  background: linear-gradient(180deg, rgba(91, 103, 216, 0.14), rgba(91, 103, 216, 0.07));
  border: 1.5px solid rgba(91, 103, 216, 0.42);
  box-shadow: 0 2px 10px rgba(91, 103, 216, 0.12);
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  white-space: nowrap;
}
.nav-jianying-download:hover {
  color: #4c56c4;
  border-color: rgba(91, 103, 216, 0.65);
  background: linear-gradient(180deg, rgba(91, 103, 216, 0.2), rgba(91, 103, 216, 0.1));
  box-shadow: 0 4px 16px rgba(91, 103, 216, 0.2);
}
.nav-jianying-download:focus-visible {
  outline: 2px solid #5b67d8;
  outline-offset: 2px;
}
.theme-dark .nav-jianying-download {
  color: #a5b4fc;
  background: rgba(99, 102, 241, 0.14);
  border-color: rgba(129, 140, 248, 0.45);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
}
.theme-dark .nav-jianying-download:hover {
  color: #c7d2fe;
  border-color: rgba(165, 180, 252, 0.65);
  background: rgba(99, 102, 241, 0.22);
}
.nav-jianying-download__icon {
  flex-shrink: 0;
  color: inherit;
}

.mobile-jianying-download {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 8px;
  padding: 14px 16px;
  border-radius: 12px;
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 800;
  color: #5b67d8;
  background: linear-gradient(180deg, rgba(91, 103, 216, 0.16), rgba(91, 103, 216, 0.08));
  border: 1.5px solid rgba(91, 103, 216, 0.45);
  box-shadow: 0 2px 12px rgba(91, 103, 216, 0.14);
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}
.mobile-jianying-download:hover {
  border-color: rgba(91, 103, 216, 0.7);
  background: linear-gradient(180deg, rgba(91, 103, 216, 0.22), rgba(91, 103, 216, 0.1));
  box-shadow: 0 4px 18px rgba(91, 103, 216, 0.18);
}
.mobile-jianying-download:focus-visible {
  outline: 2px solid #5b67d8;
  outline-offset: 2px;
}
.theme-dark .mobile-jianying-download {
  color: #a5b4fc;
  background: rgba(99, 102, 241, 0.16);
  border-color: rgba(129, 140, 248, 0.5);
}
.theme-dark .mobile-jianying-download:hover {
  color: #c7d2fe;
  border-color: rgba(165, 180, 252, 0.65);
  background: rgba(99, 102, 241, 0.24);
}

.theme-toggle {
  width: 40px; height: 40px;
  border-radius: 50%;
  border: 1.5px solid var(--border);
  background: var(--bg-card);
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}
.theme-toggle:hover {
  border-color: var(--primary);
  color: var(--primary);
  background: var(--primary-light);
}
.nav-cta { font-size: 0.88rem; padding: 10px 22px; }

/* 剩余积分：主题蓝高亮胶囊 */
.nav-credits {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 100px;
  height: 38px;
  flex-shrink: 0;
  background: var(--primary-light);
  border: 1.5px solid var(--border);
  box-shadow: var(--shadow-sm);
  text-decoration: none;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}
.nav-credits:hover {
  border-color: var(--primary);
  background: color-mix(in srgb, var(--primary-light) 70%, var(--primary) 30%);
  box-shadow: 0 2px 12px color-mix(in srgb, var(--primary) 22%, transparent);
}
.theme-dark .nav-credits {
  background: rgba(37, 99, 235, 0.14);
  border-color: rgba(59, 130, 246, 0.38);
  box-shadow: 0 2px 10px rgba(37, 99, 235, 0.12);
}
.nav-credits-icon {
  color: #F59E0B;
  flex-shrink: 0;
  filter: drop-shadow(0 1px 2px rgba(245, 158, 11, 0.35));
}
.theme-dark .nav-credits-icon {
  color: #FBBF24;
  filter: drop-shadow(0 1px 3px rgba(251, 191, 36, 0.4));
}
.nav-credits-val {
  font-size: 0.92rem;
  font-weight: 800;
  color: var(--primary);
  font-variant-numeric: tabular-nums;
  line-height: 1;
  letter-spacing: 0.01em;
}

/* User avatar dropdown */
.nav-user-wrap { position: relative; }
.nav-user-btn {
  display: flex; align-items: center; gap: 6px;
  padding: 4px 8px 4px 4px;
  background: var(--bg-card); border: 1.5px solid var(--border);
  border-radius: 100px; cursor: pointer;
  transition: all 0.2s;
}
.nav-user-btn:hover { border-color: var(--primary); background: var(--primary-light); }
.nav-user-avatar {
  width: 32px; height: 32px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.nav-user-avatar--img {
  padding: 0;
  background: var(--border-light);
}
.nav-user-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.nav-user-initial { font-size: 0.9rem; font-weight: 800; color: white; }
.nav-user-btn svg { color: var(--text-muted); transition: transform 0.2s; }
.nav-user-btn svg.rotated { transform: rotate(180deg); }
.nav-user-dropdown {
  position: absolute; top: calc(100% + 4px); right: 0;
  min-width: 240px; background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: 14px; box-shadow: 0 12px 32px rgba(0,0,0,0.15);
  padding: 12px 0; z-index: 1001;
}
.nav-dropdown-header {
  display: flex; align-items: center; gap: 12px;
  padding: 0 16px 12px;
}
.nd-avatar {
  width: 44px; height: 44px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  overflow: hidden;
}
.nd-avatar--img {
  padding: 0;
  background: var(--border-light);
}
.nd-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.nd-avatar-initial { font-size: 1.1rem; font-weight: 900; color: white; line-height: 1; }
.nd-info { flex: 1; min-width: 0; }
.nd-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 2px;
}
.nd-name { font-size: 0.95rem; font-weight: 800; color: var(--text); }
.nd-member-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 100px;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  line-height: 1.3;
  max-width: 100%;
}
.nd-member-badge--normal {
  background: var(--bg-alt);
  color: var(--text-secondary);
  border: 1px solid var(--border-light);
}
.nd-member-badge--vip {
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.14), rgba(6, 182, 212, 0.1));
  color: #1d4ed8;
  border: 1px solid rgba(37, 99, 235, 0.35);
}
.nd-member-badge--svip {
  background: linear-gradient(135deg, #7c3aed, #a855f7);
  color: #fff;
  border: 1px solid rgba(167, 139, 250, 0.5);
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.22);
}
.nd-id { font-size: 0.72rem; color: var(--text-muted); margin-top: 2px; }
.nd-phone { font-size: 0.78rem; color: var(--text-secondary); margin-top: 1px; }
.nav-dropdown-divider { height: 1px; background: var(--border-light); margin: 8px 0; }
.nav-dropdown-item {
  display: flex; align-items: center; gap: 10px;
  width: 100%; padding: 10px 16px;
  font-size: 0.88rem; font-weight: 500; color: var(--text-secondary);
  text-decoration: none; background: none; border: none;
  cursor: pointer; transition: background 0.15s;
  font-family: inherit; text-align: left;
}
.nav-dropdown-item:hover { background: var(--primary-light); color: var(--primary); }
.nav-dropdown-item svg { flex-shrink: 0; }
.nav-dropdown-logout { color: var(--text-muted); }
.nav-dropdown-logout:hover { color: #EF4444; background: rgba(239,68,68,0.08); }
.dropdown-enter-active, .dropdown-leave-active { transition: opacity 0.15s, transform 0.15s; }
.dropdown-enter, .dropdown-leave-to { opacity: 0; transform: translateY(-6px); }

.hamburger {
  display: none;
  flex-direction: column;
  gap: 5px;
  width: 40px; height: 40px;
  border: none;
  background: transparent;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.hamburger span {
  display: block;
  width: 22px; height: 2px;
  background: var(--text);
  border-radius: 2px;
  transition: all 0.3s ease;
}
.hamburger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.hamburger.open span:nth-child(2) { opacity: 0; }
.hamburger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }
.mobile-menu {
  background: var(--bg-card);
  border-top: 1px solid var(--border);
  padding: 16px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.mobile-link {
  padding: 12px 16px;
  color: var(--text-secondary);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  border-radius: 10px;
  transition: all 0.2s;
}
.mobile-link:hover { background: var(--primary-light); color: var(--primary); }
.mobile-actions { margin-top: 12px; display: flex; flex-direction: column; gap: 8px; }
.mobile-link--resource {
  text-align: center;
  font-weight: 600;
  border: 1px solid var(--border-light);
  background: var(--bg-alt);
}
.mobile-credits-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 12px 16px;
  border-radius: 12px;
  background: var(--primary-light);
  border: 1.5px solid var(--border);
  box-shadow: var(--shadow-sm);
  text-decoration: none;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}
.mobile-credits-bar:hover {
  border-color: var(--primary);
  background: color-mix(in srgb, var(--primary-light) 70%, var(--primary) 30%);
}
.theme-dark .mobile-credits-bar {
  background: rgba(37, 99, 235, 0.14);
  border-color: rgba(59, 130, 246, 0.38);
}
.mobile-credits-icon {
  color: #F59E0B;
  flex-shrink: 0;
  filter: drop-shadow(0 1px 2px rgba(245, 158, 11, 0.35));
}
.theme-dark .mobile-credits-icon {
  color: #FBBF24;
}
.mobile-credits-label {
  flex: 1;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--text-secondary);
}
.mobile-credits-val {
  font-size: 1rem;
  font-weight: 800;
  color: var(--primary);
  font-variant-numeric: tabular-nums;
}

/* Transitions */
.icon-fade-enter-active, .icon-fade-leave-active { transition: all 0.2s ease; }
.icon-fade-enter, .icon-fade-leave-to { opacity: 0; transform: scale(0.7); }
.slide-down-enter-active, .slide-down-leave-active { transition: all 0.3s ease; }
.slide-down-enter, .slide-down-leave-to { opacity: 0; transform: translateY(-10px); }

@media (max-width: 900px) {
  .nav-links { display: none; }
  .nav-cta, .nav-user-wrap, .nav-credits { display: none; }
  .hamburger { display: flex; }
  .nav-jianying-download {
    padding: 7px 10px;
    font-size: 0.8rem;
    gap: 6px;
  }
  .nav-jianying-download__icon {
    width: 18px;
    height: 18px;
  }
}
@media (max-width: 480px) {
  .nav-container { padding: 0 16px; }
  .nav-jianying-download {
    font-size: 0.74rem;
    padding: 6px 8px;
  }
}
</style>
